import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Plus, X, BarChart3, Repeat, Hash } from "lucide-react";
import { toast } from "sonner";

/**
 * «Top Προϊόντα Πελάτη» — shared dialog.
 *
 * Two modes:
 *   1. **POS / Delivery quick-add** (default, when `onAddItem` is set):
 *      every item has a [+ Προσθήκη] button that adds it to the
 *      caller's cart. The customer is already attached by the picker
 *      before this dialog opens.
 *   2. **Read-only** (when `onAddItem` is null): just a stats list,
 *      used from the CustomersPage history dialog.
 *
 * Backend: GET /api/customers/{id}/top-items?limit=5
 */
export default function CustomerTopItemsDialog({
  customer,
  onOpenChange,
  onAddItem,
  open = true,
}) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!customer?.id) return;
    setBusy(true);
    http
      .get(`/customers/${customer.id}/top-items?limit=5`)
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [], total: 0 }))
      .finally(() => setBusy(false));
  }, [customer?.id]);

  if (!customer) return null;
  const items = data?.items || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="customer-top-items-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 size={20} className="text-purple-600" /> Top Προϊόντα
          </DialogTitle>
          <DialogDescription>
            {customer.name}
            {customer.code && <span className="ml-2 font-mono text-xs">#{customer.code}</span>}
            <span className="ml-2 text-slate-400">·</span>
            <span className="ml-2 text-xs">Συχνότητα × ποσότητα — από όλο το ιστορικό</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          {busy && (
            <div className="text-sm text-slate-400 italic py-6 text-center">Φόρτωση…</div>
          )}
          {!busy && items.length === 0 && (
            <Card className="p-4 bg-amber-50 border-amber-300">
              <div className="text-sm text-amber-900 flex items-center gap-2">
                <Repeat size={14} /> Ο πελάτης δεν έχει ακόμη παραγγείλει.
              </div>
            </Card>
          )}
          {items.map((it, idx) => {
            const stats = it._stats || {};
            return (
              <div
                key={idx}
                className="bg-white border-2 border-slate-200 hover:border-purple-400 rounded-md px-3 py-2.5 flex items-center gap-3 transition-all"
                data-testid={`top-item-row-${idx}`}
              >
                <div className="flex items-center justify-center w-9 h-9 rounded-full bg-purple-100 text-purple-700 font-bold shrink-0">
                  {idx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-slate-900 truncate" data-testid={`top-item-desc-${idx}`}>
                    {it.description}
                  </div>
                  <div className="text-xs text-slate-500 flex items-center gap-3 mt-0.5 font-mono">
                    <span>×{stats.total_qty || 0} τμχ</span>
                    <span>·</span>
                    <span>{stats.frequency || 0} παραγγ.</span>
                    {it.price > 0 && (
                      <>
                        <span>·</span>
                        <span className="text-emerald-700">{Number(it.price).toFixed(2)}€</span>
                      </>
                    )}
                  </div>
                </div>
                {onAddItem ? (
                  <Button
                    size="sm"
                    onClick={() => {
                      onAddItem(it);
                      toast.success(`+ ${it.description}`);
                    }}
                    className="bg-purple-600 hover:bg-purple-700 text-white font-bold"
                    data-testid={`top-item-add-${idx}`}
                  >
                    <Plus size={14} className="mr-1" /> Προσθήκη
                  </Button>
                ) : (
                  <div className="text-right shrink-0">
                    <div className="text-xs text-purple-700 font-mono font-bold">
                      score {Number(stats.score || 0).toFixed(0)}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="customer-top-items-close"
          >
            <X size={14} className="mr-1" /> Κλείσιμο
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Re-export the Hash icon so consumers can match the picker chip style.
export { Hash };
